import { memo } from "react";

import type {
  AttributeCollectionId,
  AttributeGroup as AttributeGroupType,
  FieldId,
  FieldValueType,
} from "../../types";
import { AttributeGroup } from "../groups/AttributeGroup";

interface DynamicGroupValue {
  collectionIndex: number;
  groupIndex: number;
  values: Record<FieldId, FieldValueType>;
}

// 表示用に拡張した動的グループ値の型
interface ExtendedDynamicGroupValue extends DynamicGroupValue {
  displayIndex: number;
}

// 動的グループのデータと関連情報を格納する型
export interface DynamicGroupData {
  group: AttributeGroupType;
  fieldValues: ExtendedDynamicGroupValue[];
}

/**
 * 動的グループの表示を責務とするコンポーネント
 */
interface DynamicGroupRendererProps {
  dynamicGroup: DynamicGroupData | undefined;
  collectionId: AttributeCollectionId;
  collectionIndex: number;
  layout?: "vertical" | "horizontal";
  readOnly?: boolean;
}

export const DynamicGroupRenderer = memo(
  ({
    dynamicGroup,
    collectionId,
    collectionIndex,
    layout = "vertical",
    readOnly,
  }: DynamicGroupRendererProps) => {
    if (!dynamicGroup) return null;

    const { group, fieldValues } = dynamicGroup;

    // fieldValuesが空の場合は、初期グループを表示する
    if (fieldValues.length === 0) {
      return (
        <AttributeGroup
          key={`${collectionId}-${group.id}-0`}
          collectionId={collectionId}
          collectionIndex={collectionIndex}
          groupId={group.id}
          groupIndex={0}
          layout={layout}
          data-testid={`group-${group.id}-0`}
          readOnly={readOnly}
        />
      );
    }

    // fieldValuesに基づいて動的グループを表示
    return (
      <>
        {fieldValues.map((fieldValue) => (
          <AttributeGroup
            key={`${collectionId}-${group.id}-${fieldValue.displayIndex}`}
            collectionId={collectionId}
            collectionIndex={collectionIndex}
            groupId={group.id}
            groupIndex={fieldValue.groupIndex}
            layout={layout}
            data-testid={`group-${group.id}-${fieldValue.displayIndex}`}
            readOnly={readOnly}
          />
        ))}
      </>
    );
  },
);

DynamicGroupRenderer.displayName = "DynamicGroupRenderer";
